// Rendu HTML d'une lettre de motivation à partir d'un objet structuré.
// Mêmes invariants que `resumeHtml.ts` :
//  - mono-colonne, ASCII-safe pour les parsers ;
//  - CSS inline (utilisable en `iframe srcdoc` + impression native) ;
//  - couleur d'accent partagée avec le CV (cohérence visuelle).

import { escapeHtml, resolveRenderSettings, type RenderSettings, type SenderIdentity } from './resumeHtml'
import type { Basics } from './schema'
import { documentBaseName } from './documentName'
import { buildRevisionBridgeScript } from './revisionBridge'

export interface CoverLetter {
  /** Formule d'ouverture, ex : "Madame, Monsieur," */
  greeting: string
  /** Corps de la lettre, un paragraphe par entrée. */
  paragraphs: string[]
  /** Formule de politesse, ex : "Cordialement,". */
  closing: string
  /** Signature (nom du candidat). */
  signature: string
}

export function emptyCoverLetter(): CoverLetter {
  return { greeting: '', paragraphs: [], closing: '', signature: '' }
}

/**
 * Normalise la sortie IA (qui peut omettre des champs / renvoyer un tableau
 * mal typé) vers la forme stricte `CoverLetter`.
 */
export function normalizeCoverLetter(raw: unknown): CoverLetter {
  const r = (raw ?? {}) as Record<string, unknown>
  const paragraphs = Array.isArray(r.paragraphs)
    ? r.paragraphs.map((p) => (typeof p === 'string' ? p.trim() : '')).filter((p) => p.length > 0)
    : []
  return {
    greeting: typeof r.greeting === 'string' ? r.greeting.trim() : '',
    paragraphs,
    closing: typeof r.closing === 'string' ? r.closing.trim() : '',
    signature: typeof r.signature === 'string' ? r.signature.trim() : '',
  }
}

/**
 * Retire les tirets d'incise d'un texte écrit par l'IA.
 *
 * Le tiret cadratin (« — ») et le tiret entouré d'espaces (« - ») sont la
 * signature d'un texte de modèle : un recruteur les repère tout de suite. La
 * consigne du prompt ne suffit pas (le modèle en remet), d'où ce passage en
 * dur. Le trait d'union d'un mot composé (« peut-être ») n'est pas touché ;
 * entre deux nombres (« 2019 – 2022 »), le tiret devient un simple trait.
 */
export function sansTiretsIncise(texte: string): string {
  return texte
    .replace(/^\s*[-–—]\s+/, '')
    .replace(/\s*[—–]\s*$|\s+-\s*$/, '')
    .replace(/(\d)\s*[–—]\s*(\d)/g, '$1-$2')
    .replace(/(\d) - (\d)/g, '$1-$2')
    .replace(/\s*[—–]\s*|\s+-\s+/g, ', ')
    .replace(/,\s*([,.;:!?])/g, '$1')
}

/** `sansTiretsIncise` sur toute la lettre. À appliquer à la sortie de l'IA
 *  seulement : une retouche à la main garde les tirets que le candidat a tapés. */
export function lettreSansTirets(letter: CoverLetter): CoverLetter {
  return {
    ...letter,
    greeting: sansTiretsIncise(letter.greeting),
    paragraphs: letter.paragraphs.map(sansTiretsIncise),
    closing: sansTiretsIncise(letter.closing),
  }
}

/**
 * Extrait les coordonnées de l'expéditeur d'un profil, pour l'en-tête `sender`.
 *
 * Vit ici plutôt que dans chaque app : les deux produisent des lettres, et le
 * jour où l'une oublierait le téléphone, la différence ne se verrait que sur le
 * document envoyé. Un profil absent rend un objet vide — le bloc s'omet, il ne
 * se remplit pas de trous.
 */
export function senderFromProfile(profile?: { basics?: Partial<Basics> } | null): SenderIdentity {
  const b = profile?.basics
  return { name: b?.name ?? '', email: b?.email ?? '', phone: b?.phone ?? '', location: b?.location ?? '' }
}

function buildCss(accentHex: string): string {
  const accent = `#${accentHex}`
  return `
    :root { --accent: ${accent}; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      font-family: 'Helvetica Neue', Arial, 'Liberation Sans', sans-serif;
      font-size: 11pt;
      line-height: 1.55;
      color: #1a1a1a;
      background: #ffffff;
    }
    .letter {
      max-width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 22mm 22mm;
      background: #fff;
    }
    .letter[contenteditable="true"] { outline: none; }
    .letter[contenteditable="true"] :focus {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
      border-radius: 2px;
    }
    .letter h1 {
      font-size: 16pt;
      font-weight: 700;
      color: var(--accent);
      margin: 0 0 14pt 0;
      border-bottom: 1pt solid var(--accent);
      padding-bottom: 4pt;
    }
    /* Bloc expéditeur : corps de texte, aucun accent, aucun filet. C'est tout
       l'intérêt — une lettre ne se titre pas, elle s'en-tête. */
    .sender {
      font-style: normal;
      margin: 0 0 16pt 0;
      line-height: 1.4;
    }
    .sender .sender-name { font-weight: 600; }
    .sender div { margin: 0; }
    .greeting { margin: 0 0 12pt 0; }
    .paragraph { margin: 0 0 10pt 0; text-align: justify; }
    .closing { margin: 14pt 0 4pt 0; }
    .signature { margin: 0; font-weight: 600; }
    @page { size: A4; margin: 0; }
    @media print {
      html, body { background: #fff; }
      .letter {
        margin: 0;
        box-shadow: none;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .letter :focus { outline: none !important; }
    }
    .paragraph, .greeting, .closing, .signature {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  `
}

/**
 * En-tête de la lettre — trois états, choisis par l'utilisateur (cf. `LetterHeader`).
 *
 * Le défaut est `none` : la lettre s'ouvre sur la formule d'appel. L'ancien
 * comportement (le nom du candidat en gros titre coloré, souligné d'un filet,
 * exactement comme le titre d'un CV) reste disponible sous `name`, mais n'est
 * plus le défaut — personne ne titre une lettre de son propre nom, et il figure
 * déjà en signature.
 *
 * Le bloc `sender` ne renvoie RIEN si aucune coordonnée n'est fournie : un
 * en-tête vide serait pire que pas d'en-tête. Et il n'y a délibérément pas de
 * bloc destinataire — on n'a ni l'adresse de l'entreprise ni le nom du
 * recruteur, et un demi-en-tête de lettre officielle serait plus étrange que
 * pas d'en-tête du tout.
 */
function renderLetterHeader(letter: CoverLetter, cfg: ReturnType<typeof resolveRenderSettings>): string {
  if (cfg.letterHeader === 'name') {
    return letter.signature ? `<h1>${escapeHtml(letter.signature)}</h1>` : ''
  }
  if (cfg.letterHeader !== 'sender') return ''

  // Le nom vient de la signature à défaut du profil : c'est la même personne, et
  // la lettre est parfois rendue sans que l'appelant ait passé `sender`.
  const nom = cfg.sender.name || letter.signature
  const lignes = [
    nom ? `<div class="sender-name">${escapeHtml(nom)}</div>` : '',
    cfg.sender.location ? `<div>${escapeHtml(cfg.sender.location)}</div>` : '',
    cfg.sender.email ? `<div>${escapeHtml(cfg.sender.email)}</div>` : '',
    cfg.sender.phone ? `<div>${escapeHtml(cfg.sender.phone)}</div>` : '',
  ].filter(Boolean)
  return lignes.length ? `<address class="sender">${lignes.join('')}</address>` : ''
}

/**
 * Rend la lettre en HTML complet (doctype/head/body), prêt pour `iframe.srcdoc`
 * et pour l'impression PDF native du navigateur (cohérent avec `resumeHtml`).
 */
export function renderCoverLetterToHTML(letter: CoverLetter, settings?: RenderSettings | unknown): string {
  const cfg = resolveRenderSettings(settings)
  const css = buildCss(cfg.accentColor)
  const editAttr = cfg.editable ? ' contenteditable="true" spellcheck="false"' : ''

  const paragraphsHtml = letter.paragraphs
    .map((p) => `<p class="paragraph">${escapeHtml(p)}</p>`)
    .join('\n')

  const heading = renderLetterHeader(letter, cfg)
  const greetingHtml = letter.greeting ? `<p class="greeting">${escapeHtml(letter.greeting)}</p>` : ''
  const closingHtml = letter.closing ? `<p class="closing">${escapeHtml(letter.closing)}</p>` : ''
  const signatureHtml = letter.signature ? `<p class="signature">${escapeHtml(letter.signature)}</p>` : ''

  // Bridge de sélection -> retouches IA ad-hoc (Phase 5), uniquement en mode éditable.
  const revisionScript = cfg.editable ? `<script>${buildRevisionBridgeScript()}</script>` : ''

  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(documentBaseName({ candidate: letter.signature, company: cfg.company }, 'lettre'))}</title>
<style>${css}</style>
${revisionScript}
</head>
<body>
<div class="letter"${editAttr}>
${heading}
${greetingHtml}
${paragraphsHtml}
${closingHtml}
${signatureHtml}
</div>
</body>
</html>`
}
